import * as React from 'react';
import { styled, List, Button, Typography, Divider, Collapse, Paper, Icon, Tooltip, IconButton } from '@mui/material';
import { UserAccount } from './UserAccount';
import { NewUserAccount } from './NewUserAccount';
import { useAuth } from '../hooks';
import { AccountInfo } from '@azure/msal-browser';
import { useAppDispatch } from 'store';
import { useNavigate } from 'react-router-dom';
import * as AuthStore from '../store/Auth';
import { RouteContext } from 'App';
import FolderHierarchy from './FolderHierarchy';
import { DialogProviderContext } from './DialogProvider';
import CreateFolderButton from './CreateFolderButton';
import RefreshIcon from '@mui/icons-material/Refresh'
import * as FolderStore from 'store/Folder';

const drawerWidth = 300;

const FoldersContainer = styled('div')({
    flex: 1
});

const AccountsContainer = styled(List)(({ theme }) => ({
    padding: 0,
    transition: 'height 2s',
    transitionTimingFunction: 'ease-in-out'
}));

const AccountsFooter = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end'
}));

const AccountsFooterButton = styled(Button)(({ theme }) => ({
    fontSize: 13,
    borderRadius: 0
}));

const ToolbarText = styled(Typography)(({ theme }) => ({
    fontSize: theme.spacing(2),
    letterSpacing: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    filter: `drop-shadow(0px 0px 2px ${theme.palette.primary.main})`,
    textTransform: 'uppercase',
    flexGrow: 1
}));

const DrawerContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
});

const ToolbarDiv = styled('div')(({ theme }) => ({
    ...theme.mixins.toolbar,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center'
}));

const ToolsDiv = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end'
});

const GrowDiv = styled('div')({
    flexGrow: 1
});

const NavDrawer = () => {
    const auth = useAuth();
    const [accountExpanded, setAccountExpanded] = React.useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    function handleLogout() {
        dispatch(AuthStore.actionCreators.logout());
    }

    function handleLogin() {
        dispatch(AuthStore.actionCreators.login());
    }

    function handleAccountExpandToggle() {
        setAccountExpanded(e => !e);
    }

    function handleAccountCollapse() {
        setAccountExpanded(false);
    }

    function handleSwitchAccount(account: AccountInfo) {
        dispatch(AuthStore.actionCreators.switchAccount(account.username));
    }

    const handleNodeSelected = (nodeType: 'folder' | 'image', nodeId: string) => {
        switch (nodeType) {
            case 'folder': {
                navigate(`/folder/${nodeId}`);
                break;
            }
            case 'image': {
                navigate(`/image/${nodeId}`);
                break;
            }
        }
    }

    const handleRefreshFolders = () => {
        dispatch(FolderStore.actionCreators.loadFolders());
    }

    const userControlFooterHeight = 37;
    const userButtonHeight = 56;
    const userControlHeight = accountExpanded ? (userButtonHeight * 2) + (auth.otherAccounts.length * userButtonHeight) + userControlFooterHeight : userButtonHeight;
    const headerHeight = 64;

    return (
        <DialogProviderContext.Consumer>
            {(dialogContext) =>
                <RouteContext.Consumer>
                    {(ctx) =>
                        <DrawerContainer>
                            <ToolbarDiv>
                                <ToolbarText variant='subtitle1' noWrap>
                                    IMAGE HUB
                                </ToolbarText>
                                <ToolsDiv>
                                    <Tooltip title='Refresh folders'>
                                        <IconButton onClick={handleRefreshFolders}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <CreateFolderButton />
                                </ToolsDiv>
                            </ToolbarDiv>
                            <FoldersContainer style={{ maxHeight: `calc(100% - ${userControlHeight + headerHeight}px)` }}>
                                <FolderHierarchy foldersOnly selectedNodeId={ctx.id} onNodeSelected={handleNodeSelected} />
                            </FoldersContainer>
                            <Collapse in={accountExpanded} collapsedSize={'56px'} timeout={0}>
                                <Paper>
                                    <AccountsContainer>
                                        {auth.currentAccount && (
                                            <UserAccount account={auth.currentAccount} current={true} showCurrent={accountExpanded} onClick={handleAccountExpandToggle} />
                                        )}
                                    </AccountsContainer>
                                    <Divider />
                                    <AccountsContainer>
                                        <>
                                            {auth.otherAccounts.map(acct => {
                                                return (
                                                    <div key={acct.username}>
                                                        <UserAccount account={acct} current={false} onClick={handleSwitchAccount} />
                                                        <Divider />
                                                    </div>
                                                );
                                            })}
                                            <NewUserAccount onClick={handleLogin} />
                                        </>
                                    </AccountsContainer>
                                    <AccountsFooter>
                                        <AccountsFooterButton onClick={handleAccountCollapse}>
                                            <Icon>clear</Icon>
                                        </AccountsFooterButton>
                                        <GrowDiv></GrowDiv>
                                        <AccountsFooterButton onClick={handleLogout}>
                                            <Icon>logout</Icon> Sign Out
                                        </AccountsFooterButton>
                                    </AccountsFooter>
                                </Paper>
                            </Collapse>
                        </DrawerContainer>
                    }
                </RouteContext.Consumer>
            }
        </DialogProviderContext.Consumer>
    );
};

export default NavDrawer;